import { banco } from '../db/banco'
import type { Representada, SubPedido } from '../db/tipos'
import { gravarLocalEEnfileirar } from '../sync/outbox'
import { arredondar2 } from './precos'
import { sincronizarLancamento } from '../comissoes/lancamentos'
import { aprenderTabelasDoPedido } from './aprenderTabela'
import { registrarAjuste } from './ajustes'
import { registrarVisita } from '../visitas/logica'
import { recalcularImpostoDoSubPedido } from './impostoInformativo'

export interface FechamentoSubPedido {
  condicao_pagamento_id: string | null
  // REDESPACHO entrou no Projeto B (spec 2026-09-06), ao lado do CIF/FOB de
  // sempre -- vale para toda representada, nao so para quem calcula imposto.
  frete_tipo: 'CIF' | 'FOB' | 'REDESPACHO' | null
  transportadora: string | null
  previsao_entrega: string | null
  // Spec 27/09 (Parte 3): NOME da forma, escolhida junto com a condicao.
  forma_pagamento: string | null
}

export interface TotalDaMarca {
  representadaId: string
  representadaNome: string
  total: number
  minimo: number
  faltam: number
}

// O minimo e da MARCA, nao do sub-pedido: desde que o sub-pedido virou
// "um por marca+tabela", uma marca pode ter varios sub-pedidos no mesmo pedido.
export function totaisPorMarca(
  subPedidos: SubPedido[],
  representadas: Map<string, Representada>,
): TotalDaMarca[] {
  const somaPorMarca = new Map<string, number>()
  for (const subPedido of subPedidos) {
    const acumulado = somaPorMarca.get(subPedido.representada_id) ?? 0
    somaPorMarca.set(subPedido.representada_id, acumulado + subPedido.valor_total)
  }

  const totais: TotalDaMarca[] = []
  for (const [representadaId, total] of somaPorMarca) {
    const representada = representadas.get(representadaId)
    const minimo = representada?.pedido_minimo_valor
    if (minimo == null) continue
    totais.push({
      representadaId,
      representadaNome: representada!.nome,
      total: arredondar2(total),
      minimo,
      faltam: total < minimo ? arredondar2(minimo - total) : 0,
    })
  }
  return totais
}

export function verificarMinimos(
  subPedidos: SubPedido[],
  representadas: Map<string, Representada>,
): Array<{ representadaId: string; representadaNome: string; faltam: number }> {
  return totaisPorMarca(subPedidos, representadas)
    .filter((marca) => marca.faltam > 0)
    .map(({ representadaId, representadaNome, faltam }) => ({
      representadaId, representadaNome, faltam,
    }))
}

// "Se eu aplicar este corte, a marca fica abaixo do minimo da fabrica?"
// Responde ANTES de gravar: a tela usa isto para travar o Confirmar do ajuste,
// do mesmo jeito que o fechamento trava o envio. Reusa verificarMinimos de
// proposito -- o minimo e da MARCA (varios sub-pedidos por tabela somam), e
// duplicar essa soma aqui seria a quarta regressao da mesma familia.
export function faltaParaOMinimo(
  subPedidos: SubPedido[],
  representadas: Map<string, Representada>,
  subPedidoId: string,
  novoTotal: number,
): { representadaNome: string; faltam: number } | null {
  const alvo = subPedidos.find((s) => s.id === subPedidoId)
  if (!alvo) return null

  const projetados = subPedidos
    // Cancelado guarda o valor_total que tinha, mas a fabrica nao vai produzir
    // aquilo: deixa-lo na soma esconderia a falta de minimo.
    .filter((s) => s.id === subPedidoId || s.status !== 'cancelado')
    .map((s) => (s.id === subPedidoId ? { ...s, valor_total: novoTotal } : s))

  const daMarca = totaisPorMarca(projetados, representadas).find(
    (t) => t.representadaId === alvo.representada_id,
  )
  if (!daMarca || daMarca.faltam === 0) return null
  // Marca inteira fora (total projetado zero): tirar o ultimo item CANCELA o
  // sub-pedido. Cancelamento nao e "vendeu abaixo do minimo", e "nao vendeu" --
  // avisar sobre minimo aqui seria ruido sobre uma decisao ja tomada.
  if (daMarca.total === 0) return null
  return { representadaNome: daMarca.representadaNome, faltam: daMarca.faltam }
}

// Ordem canonica do ciclo de vida; cancelado e um ramo lateral (nao entra aqui).
const ORDEM_STATUS: ReadonlyArray<SubPedido['status']> = [
  'rascunho', 'fechado', 'enviado', 'faturado', 'entregue', 'pago',
]

export async function mudarStatusSubPedido(
  subPedido: SubPedido,
  novoStatus: SubPedido['status'],
  extras: Partial<SubPedido> = {},
): Promise<void> {
  // Guardas de invariante: status terminal nao muda; status so avança (+ cancelar de status ativo).
  if (subPedido.status === 'cancelado' || subPedido.status === 'pago') {
    throw new Error(`Sub-pedido ${subPedido.letra} está "${subPedido.status}": status imutável.`)
  }
  // Entregue nao cancela: a mercadoria chegou ao cliente, falta so o dinheiro.
  // "Nota cancelada" e o ato do estagio FATURADO, nao do entregue.
  if (novoStatus === 'cancelado' && subPedido.status === 'entregue') {
    throw new Error(
      `Sub-pedido ${subPedido.letra} está "entregue": a mercadoria já chegou, não dá para cancelar a nota.`,
    )
  }
  if (novoStatus !== 'cancelado') {
    const atual = ORDEM_STATUS.indexOf(subPedido.status)
    const alvo = ORDEM_STATUS.indexOf(novoStatus)
    if (alvo <= atual) {
      throw new Error(`Transição inválida: ${subPedido.status} → ${novoStatus} (status só avança).`)
    }
  }
  // As datas nascem na transicao (0040). `extras` pode trazer a sua, mas so
  // preenchemos quando ainda nao ha -- reenviar nao reescreve o dia do envio.
  const datas: Partial<SubPedido> = {}
  if (novoStatus === 'fechado' && !subPedido.fechado_em) datas.fechado_em = new Date().toISOString()
  if (novoStatus === 'enviado' && !subPedido.enviado_em) datas.enviado_em = new Date().toISOString()
  await gravarLocalEEnfileirar('sub_pedidos', { ...subPedido, ...datas, ...extras, status: novoStatus })
  await sincronizarLancamento(subPedido.id)
}

// Cancelar uma marca inteira e sempre o mesmo par: registrar a perda no
// historico E mudar o status. Ficava solto na tela do pedido; o Ciclo 2 criou um
// SEGUNDO lugar que cancela (a "nota cancelada", na conferencia da entrega), e
// par duplicado e par que um dia diverge.
//
// A transicao vem PRIMEIRO de proposito: ela e quem recusa (entregue nao
// cancela), e gravar o ajuste antes deixaria uma perda registrada para um
// cancelamento que nao aconteceu.
export async function cancelarSubPedido(subPedido: SubPedido, motivo: string): Promise<void> {
  await mudarStatusSubPedido(subPedido, 'cancelado', { motivo_cancelamento: motivo })
  await registrarAjuste({
    sub_pedido_id: subPedido.id,
    escopo: 'sub_pedido',
    item_pedido_id: null,
    produto_id: null,
    quantidade_antes: null,
    quantidade_depois: null,
    valor_antes: subPedido.valor_total,
    valor_depois: 0,
    motivo,
  })
}

export async function fecharPedido(
  pedidoId: string,
  fechamentos: Record<string, FechamentoSubPedido>,
): Promise<void> {
  const subPedidos = await banco.sub_pedidos
    .where('pedido_id')
    .equals(pedidoId)
    .filter((s) => s.apagado_em === null && s.status === 'rascunho')
    .toArray()
  for (const subPedido of subPedidos) {
    const fechamento = fechamentos[subPedido.id]
    await mudarStatusSubPedido(subPedido, 'fechado', fechamento ?? {})
    // Frete/condicao acabaram de ser gravados: o imposto informativo do item
    // passa a considerar os dois (o preco de venda nao muda).
    await recalcularImpostoDoSubPedido(subPedido.id)
  }
  const pedido = await banco.pedidos.get(pedidoId)
  if (pedido) await gravarLocalEEnfileirar('pedidos', { ...pedido, status: 'fechado' })
  await aprenderTabelasDoPedido(pedidoId)

  // Fechar pedido E a prova de que a visita aconteceu: o representante nao
  // deveria ter de contar de novo ao app o que ele acabou de fazer. Aqui, e nao
  // na tela, porque este e o funil unico por onde todo pedido passa para fechar.
  //
  // O pedidoId vai EXPLICITO: quem esta aqui sabe qual pedido gerou a venda, e
  // deixar a visita adivinhar por data erra no pedido aberto ontem.
  if (pedido) await registrarVisita(pedido.cliente_id, 'com_pedido', null, null, pedidoId)
}
